import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Check, Trash2, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const errMsg = (e) => e.response?.data?.detail || 'Operazione non riuscita';

const SuggestionCard = ({ s, onDone }) => {
  const [q, setQ] = useState(s.question);
  const [a, setA] = useState(s.answer);
  const [busy, setBusy] = useState('');
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'publish') {
        if (q !== s.question || a !== s.answer) await axios.put(`${API}/admin/faq-suggestions/${s.id}`, { question: q, answer: a }, auth());
        await axios.post(`${API}/admin/faq-suggestions/${s.id}/publish`, {}, auth());
      } else {
        await axios.delete(`${API}/admin/faq-suggestions/${s.id}`, auth());
      }
      onDone();
    } catch (e) {
      alert(errMsg(e));
      setBusy('');
    }
  };
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm space-y-3" data-testid={`faq-suggestion-${s.id}`}>
      <Input value={q} onChange={(e) => setQ(e.target.value)} className="font-medium text-[#283880]" data-testid={`faq-suggestion-question-${s.id}`} />
      <Textarea value={a} onChange={(e) => setA(e.target.value)} rows={4} className="text-sm" data-testid={`faq-suggestion-answer-${s.id}`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!!busy || !q.trim() || !a.trim()} onClick={() => run('publish')} className="bg-green-600 hover:bg-green-700" data-testid={`faq-suggestion-publish-${s.id}`}>
          {busy === 'publish' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
          {busy === 'publish' ? 'Traduzione e pubblicazione…' : 'Pubblica in 4 lingue'}
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => window.confirm('Scartare questa proposta?') && run('discard')} className="text-red-600 border-red-200" data-testid={`faq-suggestion-discard-${s.id}`}>
          <Trash2 className="w-4 h-4 mr-1" /> Scarta
        </Button>
      </div>
    </div>
  );
};

const PublishedList = ({ items, onDone }) => (
  <div className="bg-white rounded-xl shadow-sm divide-y" data-testid="faq-published-list">
    {items.length === 0 && <p className="p-5 text-sm text-slate-400">Nessuna FAQ aggiunta dal pannello.</p>}
    {items.map((f) => (
      <div key={f.id} className="p-4 flex items-start justify-between gap-4" data-testid={`faq-published-${f.id}`}>
        <div>
          <p className="font-medium text-sm text-[#283880]">{f.it.q}</p>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Globe className="w-3 h-3" /> {f.en?.q}</p>
        </div>
        <Button size="sm" variant="outline" className="text-red-600 border-red-200 shrink-0" data-testid={`faq-unpublish-${f.id}`}
          onClick={async () => { if (window.confirm('Togliere questa FAQ dal sito?')) { await axios.delete(`${API}/admin/faq-published/${f.id}`, auth()); onDone(); } }}>
          Togli dal sito
        </Button>
      </div>
    ))}
  </div>
);

export const AdminFaqTab = () => {
  const { t } = useLanguage();
  const [suggestions, setSuggestions] = useState([]);
  const [published, setPublished] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [s, p] = await Promise.all([axios.get(`${API}/admin/faq-suggestions`, auth()), axios.get(`${API}/admin/faq-published`, auth())]);
    setSuggestions(s.data);
    setPublished(p.data);
  };
  useEffect(() => { load(); }, []);
  const generate = async () => {
    setBusy(true);
    try {
      const items = t('faq.items');
      await axios.post(`${API}/admin/faq-suggestions/generate`, { existing: Array.isArray(items) ? items.map((i) => i.q) : [] }, auth());
      await load();
    } catch (e) {
      alert(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-8" data-testid="admin-faq-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#283880] mb-1">FAQ suggerite</h2>
          <p className="text-sm text-slate-500 max-w-2xl">Proposte create dall'AI in base ai temi più chiesti all'assistente. Le controlli e corregga: pubblicando, vengono tradotte in inglese, tedesco e francese e compaiono nella pagina FAQ.</p>
        </div>
        <Button onClick={generate} disabled={busy} className="bg-[#C9A227] hover:bg-[#b8911f]" data-testid="faq-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {busy ? 'Sto preparando le proposte…' : 'Proponi nuove FAQ'}
        </Button>
      </div>
      {suggestions.length === 0 && <p className="text-sm text-slate-400" data-testid="faq-suggestions-empty">Nessuna proposta in attesa. Clicchi "Proponi nuove FAQ".</p>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {suggestions.map((s) => <SuggestionCard key={s.id} s={s} onDone={load} />)}
      </div>
      <div>
        <h3 className="text-lg font-bold text-[#283880] mb-3">Pubblicate dal pannello</h3>
        <PublishedList items={published} onDone={load} />
      </div>
    </div>
  );
};
